import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Icon } from "@iconify/react";
import { Upload } from "antd";
import { AppModal, Input } from "@/shared/components/dialog";
import { Button } from "@/shared/components/button";
import { cn } from "@/shared/lib/utils";
import {
  uploadImagingStudyBatch,
  getImagingBatchStatus,
  listImagingSeries,
  ImagingBatchUploadError,
  ImagingStudiesApiError,
  type ImagingBatchStatus,
  type ImagingSeries,
} from "@/portals/patient/api/imagingStudiesApi";
import { openImagingTab, openSeriesViewer } from "./imagingNavigation";
import { useImagingStudies } from "@/portals/patient/context/ImagingStudiesContext";
import { startBackoffPolling, type BackoffPollerHandle } from "@/shared/lib/pollingBackoff";
import { UPLOAD_STATUS_POLL_MS, POLL_BACKOFF_MAX_MS } from "@/features/patient/records/utils/pollingConfig";

type StagedStatus = "staged" | "uploading" | "processing" | "success" | "failed";

/** The backend caps a single file AND a whole batch's total at the same
 *  100MB ceiling (IMAGING_STUDY_MAX_FILE_SIZE_BYTES / the batch-total cap in
 *  app/config.py — one universal number, by explicit product decision), so
 *  one constant here covers both the per-file and the running-total checks
 *  below. */
const MAX_DICOM_TOTAL_BYTES = 100 * 1024 * 1024;

interface StagedFile {
  id: string;
  file: File;
  status: StagedStatus;
  /** True when this single file's own size already exceeds
   *  MAX_DICOM_TOTAL_BYTES. Still staged (visible, part of the running
   *  total) rather than silently dropped, so the patient sees exactly which
   *  file is the problem — but blocks Upload until removed. */
  oversized: boolean;
  /** Set once a terminal per-file result has landed for this file (status
   *  "success"/"failed") — reason for a failure, imagingStudyId for a
   *  success (drives the "View" button). */
  reason?: string | null;
  imagingStudyId?: string | null;
  /** Zip rows only: how many of the images inside imported / failed. */
  imageCounts?: { success: number; failed: number };
}

/** Mirrors the backend's own rule (imaging_studies/router.py): a batch is a
 *  zip batch only when it is exactly one file named *.zip. Every per-file
 *  result then belongs to that one row — and is named after a file INSIDE
 *  the zip, never after the zip itself, so filename matching can't work. */
function isSingleZipUpload(files: StagedFile[]): boolean {
  return files.length === 1 && files[0].file.name.toLowerCase().endsWith(".zip");
}

/** Rewrites the backend's exact per-file failure reasons (imaging_studies/service.py)
 *  into plain language a patient can act on, instead of the raw backend string. */
function friendlyFailureReason(reason: string | null | undefined): string {
  switch (reason) {
    case "not a valid DICOM file":
      return "This is not a DICOM image. Please upload a DICOM (.dcm) file.";
    case "corrupted or unreadable DICOM file":
      return "This DICOM file is corrupted or unreadable. Please upload a different file.";
    case "exceeds maximum file size":
      return "This file is too large. Please upload a DICOM file up to 100 MB.";
    case "file is empty":
      return "This file is empty. Please upload a valid DICOM file.";
    default:
      return reason || "This file couldn't be processed.";
  }
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

const isRemovable = (status: StagedStatus) => status === "staged";

/** ONE row, for every status a file passes through — staged/oversized,
 *  uploading, processing (batch accepted, waiting on this file's own
 *  result), and the two terminal outcomes. This is deliberately the ONLY
 *  view a submitted file ever shows — per explicit product decision, this
 *  modal never swaps to a different screen/layout once Upload is pressed;
 *  each row already in this same list just updates its own status text (and
 *  icon) in place as real progress comes in. See ImagingUploadModal's own
 *  docstring for the two earlier, reverted designs this replaces (a
 *  full-page "Processing X of Y" takeover, and closing the modal outright). */
function FileRow({
  entry,
  batchProgress,
  batchPercent,
  onRemove,
  onView,
}: {
  entry: StagedFile;
  /** The BATCH's own live progress (status/processedCount/totalExpected), or
   *  null before any batch exists yet. Drives the "processing" row's
   *  progress bar/count/percentage below, in place of a plain status word —
   *  see this component's own docstring on the bug this replaces: a row used
   *  to flip its text straight to "Processing…" the instant its own upload
   *  POST resolved, regardless of what the batch itself had actually
   *  reported, so a row could read "Processing…" while a separate header
   *  above it simultaneously read "Queued…". Per explicit product decision,
   *  that separate header was removed entirely — the batch's live progress
   *  now renders ONLY here, inside the row, right below the file size, so
   *  there is exactly one place it can ever be read from. Once a per-file
   *  result lands (entry.status becomes "success"/"failed"), this stops
   *  mattering — that terminal text is unconditional, same as before. */
  batchProgress: BatchProgress | null;
  batchPercent: number;
  onRemove: () => void;
  onView: (imagingStudyId: string) => void;
}) {
  const isBusy = entry.status === "uploading" || entry.status === "processing";
  return (
    <div className="rounded-2xl border border-[#E5E7EB] bg-white px-4 py-3">
      <div className="flex items-center gap-3">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#F7F7F7]">
          <Icon
            icon={
              entry.status === "success"
                ? "lucide:check-circle"
                : entry.status === "failed"
                  ? "lucide:alert-circle"
                  : isBusy
                    ? "lucide:loader-2"
                    : "lucide:scan-line"
            }
            className={cn(
              "h-4 w-4",
              isBusy && "animate-spin !text-[#E4002B]",
              entry.status === "success" && "!text-[#16A34A]",
              entry.status === "failed" && "!text-[#DC2626]",
              entry.status === "staged" && "text-[#0A2947]",
            )}
          />
        </div>
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-extrabold text-[#0A2947]">{entry.file.name}</p>
          <p className="text-xs text-[#B0B0B8]">{formatFileSize(entry.file.size)}</p>

          {/* Oversized takes priority over the plain "Ready" line — same red
           *  alert-circle treatment a post-upload failure would use, via the
           *  same friendlyFailureReason copy, so a pre-upload rejection
           *  reads as the same kind of problem instead of inventing new UI
           *  language for it. */}
          {entry.status === "staged" && entry.oversized && (
            <p className="mt-1 flex items-center gap-1 text-xs font-semibold text-[#DC2626]">
              <Icon icon="lucide:alert-circle" className="h-3 w-3 shrink-0" />
              {friendlyFailureReason("exceeds maximum file size")}
            </p>
          )}
          {entry.status === "staged" && !entry.oversized && (
            <p className="mt-1 text-xs font-medium text-[#9CA3AF]">Ready — press Upload to send</p>
          )}
          {/* No spinner icon in this line — the icon-circle above already
           *  shows one (see `isBusy`). A second spinner inline in the text
           *  was a real duplicate, not a design choice. */}
          {entry.status === "uploading" && (
            <p className="mt-1 text-xs font-semibold text-[#E4002B]">Uploading...</p>
          )}
          {/* The batch's live progress — bar + "X of Y" + percentage —
           *  renders HERE, right after the file size, instead of a plain
           *  status word. This is the ONLY place it's shown (no separate
           *  header above the list any more), so it can never disagree with
           *  itself. "Queued…" until the batch has actually moved past that;
           *  anything else (including null, which shouldn't happen here in
           *  practice since batchProgress is always set in the same tick a
           *  row first becomes "processing") falls back to "Processing…". */}
          {entry.status === "processing" && (
            <div className="mt-1">
              <div className="flex items-center justify-between">
                <p className="text-xs font-semibold text-[#E4002B]">
                  {batchProgress?.status === "queued"
                    ? "Queued…"
                    : `Processing ${batchProgress?.processedCount ?? 0} of ${batchProgress?.totalExpected ?? 0}`}
                </p>
                <p className="text-xs font-semibold text-[#E4002B]">{batchPercent}%</p>
              </div>
              <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-[#F3F4F6]">
                <div
                  className="h-full rounded-full bg-[#E4002B] transition-all"
                  style={{ width: `${batchPercent}%` }}
                />
              </div>
            </div>
          )}
          {entry.status === "success" && (
            <p className="mt-1 text-xs font-semibold text-[#16A34A]">
              {entry.imageCounts
                ? `Uploaded — ${entry.imageCounts.success} image${entry.imageCounts.success === 1 ? "" : "s"} imported${entry.imageCounts.failed > 0 ? `, ${entry.imageCounts.failed} failed` : ""}`
                : "Uploaded"}
            </p>
          )}
          {entry.status === "failed" && (
            <p className="mt-1 flex items-center gap-1 text-xs font-semibold text-[#DC2626]">
              <Icon icon="lucide:alert-circle" className="h-3 w-3 shrink-0" />
              {friendlyFailureReason(entry.reason)}
            </p>
          )}
        </div>
        {entry.status === "success" && entry.imagingStudyId && (
          <Button
            variant="outline"
            onClick={() => onView(entry.imagingStudyId!)}
            className="!h-8 !shrink-0 !rounded-full !border-[#F36F8F] !px-3 !text-xs !font-extrabold !text-[#F36F8F] hover:!bg-[#FFF4F7]"
          >
            View
          </Button>
        )}
        {isRemovable(entry.status) && (
          <Button
            variant="ghost"
            onClick={onRemove}
            className="!flex !h-7 !w-7 !shrink-0 !items-center !justify-center !rounded-full !p-0 hover:!bg-[#F3F4F6]"
          >
            <Icon icon="lucide:x" className="h-4 w-4 text-[#9CA3AF]" />
          </Button>
        )}
      </div>
    </div>
  );
}

/** Live progress of the batch this modal itself just submitted — polled
 *  directly by this modal (its OWN poller, separate from
 *  ImagingStudiesContext's) purely to drive the progress bar/summary line
 *  above the file list and to merge per-file results into `files` as they
 *  land. `registerActiveBatch` is ALSO called at submit time (see
 *  handleUpload) so the batch keeps being tracked by the shared context —
 *  and "Recent DICOM Uploads" keeps showing it — if the patient closes this
 *  modal before the batch finishes; that's a safety net, not this modal's
 *  own progress source. */
interface BatchProgress {
  status: ImagingBatchStatus;
  processedCount: number;
  totalExpected: number;
}

/**
 * "+ Add" trigger's modal — enter a title for this batch, stage one or more
 * DICOM files (or a .zip of them), then Upload.
 *
 * Per explicit product decision, pressing Upload does NOT swap this modal to
 * a different screen/layout — the title field and dropzone are hidden once a
 * batch is submitted (nothing left to add to it), but the SAME file list
 * stays on screen the whole time: each row already in it just updates its
 * own status text/icon in place (staged -> uploading -> processing ->
 * success/failed), with one progress bar/summary line above the list. Two
 * earlier designs were tried and explicitly reverted before landing on this
 * one: (1) closing the modal instantly on submit and moving all progress out
 * to the page (RecentDicomUploadsCard) — reverted because the patient wanted
 * progress inside this modal; (2) keeping the modal open but swapping its
 * whole content to a separate "Processing X of Y" screen — reverted because
 * that swap read as jumping to "a new modal" rather than continuing the same
 * one. This version keeps registerActiveBatch (so "Recent DICOM Uploads"
 * still shows the batch if the patient closes this modal early) but adds no
 * screen swap of its own on top of it.
 *
 * UNLIKE the Records upload flow (RecordsUploadModal.tsx/dataPipelineApi.ts's
 * uploadMedicalZip), this endpoint has no client_request_id support and no
 * by-client-id recovery lookup — confirmed against the real backend
 * contract, not just unbuilt yet. If the POST's own response is lost to a
 * network error, there is no way to confirm whether the batch was actually
 * accepted; see handleUpload's catch block for how that's surfaced honestly
 * instead of guessing.
 */
export function ImagingUploadModal({
  open,
  onClose,
  actingAsProfileId,
}: {
  open: boolean;
  onClose: () => void;
  actingAsProfileId?: string;
}) {
  const { registerActiveBatch, refresh: refreshSeries } = useImagingStudies();
  const navigate = useNavigate();
  // Set when the modal is closed, so a completion that lands afterwards
  // doesn't navigate the patient somewhere they didn't ask to go.
  const closedRef = useRef(false);

  const [title, setTitle] = useState("");
  const [files, setFiles] = useState<StagedFile[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  // null while nothing has been submitted yet (still staging) — the single
  // flag this component uses to decide "form controls visible" vs "progress
  // bar + read-only file list" (see the render below).
  const [batchProgress, setBatchProgress] = useState<BatchProgress | null>(null);
  // This modal's OWN poller for the batch it just submitted — separate from
  // ImagingStudiesContext's poller for the same batch (see the component
  // docstring). Stopped on manual close and on unmount so it never keeps
  // ticking (and calling setState) after this modal is gone.
  const localPollerRef = useRef<BackoffPollerHandle | null>(null);
  // Set only when the most recent add attempt had to reject one or more
  // NEW files for pushing the running total over MAX_DICOM_TOTAL_BYTES —
  // purely informational (the files that DID fit stay selected and
  // uploadable), so it never disables Upload on its own. Cleared on the
  // next add attempt, or when the selection is otherwise changed enough
  // that the numbers it quotes would go stale (remove/Cancel).
  const [overflowMessage, setOverflowMessage] = useState<string | null>(null);

  // Starts fresh every time the modal is opened — closing it (X, Cancel, or
  // clicking outside) always discards whatever was staged, matching the
  // "Cancel" affordance's own meaning rather than silently resuming a
  // half-filled form days later.
  useEffect(() => {
    if (open) {
      localPollerRef.current?.stop();
      localPollerRef.current = null;
      closedRef.current = false;
      setTitle("");
      setFiles([]);
      setSubmitError(null);
      setOverflowMessage(null);
      setBatchProgress(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Belt and suspenders alongside the reset-on-open effect above: if this
  // modal instance is ever unmounted outright (not just closed) while its
  // own poller is still running, stop it — it must never keep calling
  // setState on an unmounted component.
  useEffect(() => {
    return () => {
      localPollerRef.current?.stop();
      localPollerRef.current = null;
    };
  }, []);

  /** Closing this modal (X button or backdrop click) only ever stops THIS modal's own
   *  poller — it deliberately does NOT touch the batch itself or
   *  ImagingStudiesContext's independent tracking of it, so closing early
   *  never cancels or loses progress; it just stops watching it here. */
  const handleClose = () => {
    localPollerRef.current?.stop();
    localPollerRef.current = null;
    closedRef.current = true;
    onClose();
  };

  /** Where a finished upload takes the patient: straight into the viewer if
   *  it produced exactly one series, otherwise the Imaging tab's full series
   *  list (where every new series is at the top). Only counts series — the
   *  destinations are the same ones reached by clicking a series today. */
  const goToUploadResult = async (importedIds: Set<string>) => {
    let onlySeries: ImagingSeries | null = null;
    try {
      // Newest series first, every member file included — the batch's own
      // series are at the top of the first page.
      const page = await listImagingSeries(actingAsProfileId, { limit: 50 });
      const touched = page.data.filter((s) => s.files.some((f) => importedIds.has(f.id)));
      const covered = new Set(touched.flatMap((s) => s.files.map((f) => f.id)));
      if (touched.length === 1 && [...importedIds].every((id) => covered.has(id))) onlySeries = touched[0];
    } catch {
      // Couldn't tell — the full list is always a correct place to land.
    }
    if (closedRef.current) return; // the patient already closed the modal
    handleClose();
    if (onlySeries) {
      openSeriesViewer(navigate, onlySeries);
    } else {
      refreshSeries(); // so the new series are already listed on arrival
      openImagingTab(navigate);
    }
  };

  const handleView = (imagingStudyId: string) => {
    handleClose();
    navigate(`/patient/diagnostics/imaging/${imagingStudyId}`);
  };

  /** Stages a whole selection at once (a native <input>/drop's FileList, or
   *  the full batch antd's Upload hands beforeUpload — see the Upload
   *  element below for why it's called once per SELECTION here rather than
   *  once per file). Runs entirely against local `files` state — this never
   *  uploads anything itself; it only ever decides what gets added to the
   *  staged list for the patient to review before they press Upload.
   *
   *  Two independent size checks, both against the same MAX_DICOM_TOTAL_BYTES
   *  (backend uses one ceiling for both):
   *   - a file that alone exceeds the cap is still staged, but flagged
   *     `oversized: true` (see FileRow) rather than silently dropped, so the
   *     patient can see exactly which file is the problem and remove it;
   *   - files that would only become a problem cumulatively (pushing the
   *     running total over the cap) are rejected outright and never added —
   *     whatever was already validly staged is left untouched, and
   *     `overflowMessage` explains what got left out and why. */
  const stageFiles = (newFiles: File[]) => {
    if (newFiles.length === 0) return;

    const existingTotal = files.reduce((sum, f) => sum + f.file.size, 0);
    let runningTotal = existingTotal;
    const accepted: StagedFile[] = [];
    const rejected: File[] = [];

    for (const file of newFiles) {
      const oversized = file.size > MAX_DICOM_TOTAL_BYTES;
      // An already-oversized file is judged on its own merits — it never
      // "consumes" the batch's remaining room, since it's already flagged
      // and blocking on its own regardless of what else is selected.
      if (!oversized && runningTotal + file.size > MAX_DICOM_TOTAL_BYTES) {
        rejected.push(file);
        continue;
      }
      runningTotal += file.size;
      const id = Math.random().toString(36).slice(2, 11);
      accepted.push({ id, file, status: "staged", oversized });
    }

    if (accepted.length > 0) {
      setFiles((prev) => [...prev, ...accepted]);
    }

    if (rejected.length > 0) {
      // `runningTotal` here is the total AFTER this call's accepted files
      // (if any) are folded in — the "currently selected" figure the
      // message quotes must describe the state the patient is about to
      // see, not the state from before they picked anything this time.
      const roomLeft = Math.max(MAX_DICOM_TOTAL_BYTES - runningTotal, 0);
      const subject = rejected.length === 1 ? "This file" : "These files";
      setOverflowMessage(
        `Adding these files would put you over the ${formatFileSize(MAX_DICOM_TOTAL_BYTES)} limit for one upload. ` +
          `You currently have ${formatFileSize(runningTotal)} selected, with ${formatFileSize(roomLeft)} of room left. ` +
          `${subject} ${rejected.length === 1 ? "was" : "were"} not added — remove some files, or upload them in a separate batch.`,
      );
    } else {
      setOverflowMessage(null);
    }
  };

  const handleDragOver = (e: React.DragEvent) => { e.preventDefault(); setIsDragging(true); };
  const handleDragLeave = (e: React.DragEvent) => { e.preventDefault(); setIsDragging(false); };
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    stageFiles(Array.from(e.dataTransfer.files));
  };

  const removeFile = (id: string) => {
    setFiles((prev) => prev.filter((f) => f.id !== id));
    // Both messages describe the selection as it was (the overflow one
    // quotes its byte totals; a rejected upload's error is about the files
    // that were sent) — once a file is removed they no longer apply.
    setOverflowMessage(null);
    setSubmitError(null);
  };
  const clearFiles = () => {
    setFiles([]);
    setOverflowMessage(null);
    setSubmitError(null);
  };

  const hasStagedFiles = files.some((f) => f.status === "staged");
  const hasOversizedStaged = files.some((f) => f.status === "staged" && f.oversized);
  const selectedTotalBytes = files.reduce((sum, f) => sum + f.file.size, 0);

  const handleUpload = async () => {
    const toUpload = files.filter((f) => f.status === "staged");
    if (toUpload.length === 0 || batchProgress !== null) return;
    // Defense in depth alongside the Upload button's own `disabled` — belt
    // and suspenders against ever submitting a batch containing a file this
    // same UI has already told the patient is too large.
    if (toUpload.some((f) => f.oversized)) return;
    const toUploadIds = new Set(toUpload.map((t) => t.id));
    const zipUpload = isSingleZipUpload(toUpload);

    setSubmitError(null);
    setFiles((prev) => prev.map((f) => (toUploadIds.has(f.id) ? { ...f, status: "uploading" } : f)));

    try {
      const accepted = await uploadImagingStudyBatch(toUpload.map((t) => t.file), actingAsProfileId, title);

      // Accepted. Register it with the shared context (so it's still
      // tracked/shows on "Recent DICOM Uploads" if this modal gets closed
      // early), and ALSO start this modal's own poll so this same file list
      // keeps updating live. The entered title wins as the display name if
      // given; otherwise the single file's name, or "N files" for a
      // multi-file batch — same rule as before.
      const displayName =
        title.trim() || (toUpload.length === 1 ? toUpload[0].file.name : `${toUpload.length} files`);
      registerActiveBatch(accepted.batch_id, displayName, accepted.total_expected, actingAsProfileId);

      setFiles((prev) => prev.map((f) => (toUploadIds.has(f.id) ? { ...f, status: "processing" } : f)));
      setBatchProgress({ status: "queued", processedCount: 0, totalExpected: accepted.total_expected });

      localPollerRef.current = startBackoffPolling({
        intervalMs: UPLOAD_STATUS_POLL_MS,
        maxIntervalMs: POLL_BACKOFF_MAX_MS,
        poll: async () => {
          const status = await getImagingBatchStatus(accepted.batch_id, actingAsProfileId);
          setBatchProgress({
            status: status.status,
            processedCount: status.processed_count,
            totalExpected: status.total_expected,
          });
          // Merge any per-file results that have landed so far into the
          // SAME rows already on screen, matched by filename (a streamed-in
          // batch response has no position/index field to match on — see
          // the backend's own UploadResultItem shape). A file with no
          // result yet just stays "processing".
          const batchDone = status.status === "completed" || status.status === "failed";
          setFiles((prev) =>
            prev.map((f) => {
              if (!toUploadIds.has(f.id)) return f;
              if (zipUpload) {
                // The zip row owns every result; it resolves once the batch is done.
                if (!batchDone) return f;
                const success = status.results.filter((r) => r.status === "success").length;
                const failed = status.results.filter((r) => r.status === "failed").length;
                return {
                  ...f,
                  status: success > 0 ? "success" : "failed",
                  imageCounts: { success, failed },
                  reason: success > 0 ? null : status.results.find((r) => r.reason)?.reason ?? null,
                };
              }
              const result = status.results.find((r) => r.filename === f.file.name);
              if (!result) return f;
              return { ...f, status: result.status, reason: result.reason, imagingStudyId: result.imaging_study_id };
            }),
          );
          if (batchDone) {
            localPollerRef.current?.stop();
            localPollerRef.current = null;
            const importedIds = new Set(
              status.results.flatMap((r) => (r.status === "success" && r.imaging_study_id ? [r.imaging_study_id] : [])),
            );
            // Nothing imported: stay here, where the failure is explained.
            if (importedIds.size > 0) void goToUploadResult(importedIds);
          }
        },
      });
    } catch (err) {
      // Revert to staged either way — the patient can retry, and there's no
      // in-flight batch to keep watching in either case.
      setFiles((prev) => prev.map((f) => (toUploadIds.has(f.id) ? { ...f, status: "staged" } : f)));

      // No client_request_id / by-client-id recovery exists on this backend
      // (confirmed against the real contract — see this module's own
      // docstring) — a network error means the batch's fate genuinely can't
      // be confirmed from here, so that's exactly what's told to the
      // patient, instead of silently retrying or guessing an outcome.
      if (err instanceof ImagingBatchUploadError && err.networkError) {
        setSubmitError(
          "Upload may have started but we lost the connection. Check Recent Uploads in a moment, or try again.",
        );
        return;
      }
      const message =
        err instanceof ImagingBatchUploadError || err instanceof ImagingStudiesApiError || err instanceof Error
          ? err.message
          : "Upload failed. Please check your connection and try again.";
      setSubmitError(message);
    }
  };

  const batchIsTerminal = batchProgress?.status === "completed" || batchProgress?.status === "failed";
  const batchPercent =
    batchProgress && batchProgress.totalExpected > 0
      ? Math.round((batchProgress.processedCount / batchProgress.totalExpected) * 100)
      : 0;
  // Counted in images: a zip row stands for every image inside it.
  const successCount = files.reduce((n, f) => n + (f.imageCounts ? f.imageCounts.success : f.status === "success" ? 1 : 0), 0);
  const failedCount = files.reduce((n, f) => n + (f.imageCounts ? f.imageCounts.failed : f.status === "failed" ? 1 : 0), 0);

  return (
    <AppModal
      open={open}
      onOpenChange={(next) => { if (!next) handleClose(); }}
      width={520}
      hideCloseButton
      className="[&_.ant-modal-content]:!rounded-3xl [&_.ant-modal-content]:!p-6"
    >
      <div className="flex items-start justify-between gap-4">
        <h2 className="text-base font-extrabold uppercase tracking-wide text-[#0A2947]">Xray / CT Scan / MRI</h2>
        <Button
          variant="ghost"
          onClick={handleClose}
          aria-label="Close"
          className="!flex !h-8 !w-8 !shrink-0 !items-center !justify-center !rounded-full !p-0 !text-[#0A2947] hover:!bg-[#F3F4F6] hover:!text-[#0A2947]"
        >
          <Icon icon="lucide:x" className="h-5 w-5" />
        </Button>
      </div>

      {/* Title + dropzone stay visible the WHOLE time this modal is open —
       *  per explicit product decision, nothing in this layout disappears
       *  once a batch is submitted (that was tried and felt like jumping to
       *  a different screen, even though it was technically the same
       *  modal). Once a batch exists there's nothing left to add to it, so
       *  both are just disabled instead of hidden. */}
      <div className="mt-5">
        <label htmlFor="imaging-upload-title" className="text-xs font-extrabold uppercase tracking-wide text-[#0A2947]">
          Image Title
        </label>
        <Input
          id="imaging-upload-title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Enter title"
          disabled={batchProgress !== null}
          className="!mt-2 !h-11 !rounded-xl"
        />
      </div>

      <div className="mt-5">
        <p className="text-xs font-extrabold uppercase tracking-wide text-[#0A2947]">Images of Xray / CT Scans / MRI</p>

        <div className="mt-2 flex flex-col gap-6">
          <div
            onDragOver={batchProgress === null ? handleDragOver : undefined}
            onDragLeave={batchProgress === null ? handleDragLeave : undefined}
            onDrop={batchProgress === null ? handleDrop : undefined}
            className={cn(
              "relative flex min-h-[160px] flex-col items-center justify-center rounded-xl border-2 border-dashed bg-[#FCFCFC] transition-all",
              batchProgress !== null
                ? "pointer-events-none opacity-50"
                : isDragging
                  ? "border-[#F36F8F] bg-[#FFF4F7]"
                  : "border-[#F7B4C3] hover:border-[#F36F8F]",
            )}
          >
            <Upload
              multiple
              accept=".dcm,application/dicom,.zip"
              showUploadList={false}
              disabled={batchProgress !== null}
              beforeUpload={(file, fileList) => {
                // antd calls beforeUpload once PER FILE in the selection,
                // but passes the SAME fileList (the whole batch) every
                // time — process the batch as one unit exactly once
                // (on its first file) so the running-total/overflow
                // check below sees the real batch, not one file at a
                // time with no memory of its siblings.
                if (fileList[0] === file) stageFiles(fileList as unknown as File[]);
                return false;
              }}
              className="absolute inset-0 [&_.ant-upload]:!block [&_.ant-upload]:!h-full [&_.ant-upload]:!w-full [&_.ant-upload]:!cursor-pointer"
            >
              <div className="h-full w-full" />
            </Upload>
            <Icon icon="lucide:file-text" className="h-8 w-8 !text-[#F36F8F]" />
            <p className="mt-2 text-sm font-extrabold text-[#0A2947]">
              Drop file or <span className="text-[#F17893]">Browse</span>
            </p>
          </div>
          <p className="text-xs text-[#9CA3AF]">
            DICOM (.dcm) images, single files, multiple files, or a .zip of them — up to 100 MB each.
          </p>
        </div>
      </div>

      {files.length > 0 && (
        <div className="mt-3">
          {/* Pre-submit: the live running total against the 100MB ceiling.
           *  Post-submit terminal: a succeeded/failed summary line — same
           *  slot, directly above the file list either way. The LIVE
           *  processing progress bar/count/percentage no longer has a
           *  separate slot here at all — per explicit product decision it
           *  renders once, inside each row (see FileRow), right after the
           *  file size, never as a second header above the list. */}
          {batchProgress === null ? (
            <p
              className={cn(
                "text-xs font-bold",
                selectedTotalBytes > MAX_DICOM_TOTAL_BYTES ? "text-[#DC2626]" : "text-[#6B7280]",
              )}
            >
              {formatFileSize(selectedTotalBytes)} / {formatFileSize(MAX_DICOM_TOTAL_BYTES)} selected
            </p>
          ) : batchIsTerminal && (successCount > 0 || failedCount > 0) ? (
            <div className="flex flex-wrap items-center gap-3 rounded-xl bg-[#F7F7F7] px-4 py-3 text-xs font-bold">
              {successCount > 0 && (
                <span className="flex items-center gap-1 text-[#16A34A]">
                  <Icon icon="lucide:check-circle" className="h-4 w-4" />
                  {successCount} succeeded
                </span>
              )}
              {failedCount > 0 && (
                <span className="flex items-center gap-1 text-[#DC2626]">
                  <Icon icon="lucide:alert-circle" className="h-4 w-4" />
                  {failedCount} failed
                </span>
              )}
            </div>
          ) : null}

          <div className="mt-2 space-y-2">
            {files.map((entry) => (
              <FileRow
                key={entry.id}
                entry={entry}
                batchProgress={batchProgress}
                batchPercent={batchPercent}
                onRemove={() => removeFile(entry.id)}
                onView={handleView}
              />
            ))}
          </div>
        </div>
      )}

      {batchProgress === null && overflowMessage && (
        <p className="mt-3 flex items-center gap-2 rounded-xl bg-[#FEF2F2] px-3 py-2 text-xs font-semibold text-[#DC2626]">
          <Icon icon="lucide:alert-circle" className="h-4 w-4 shrink-0" />
          {overflowMessage}
        </p>
      )}

      {batchProgress === null && submitError && (
        <p className="mt-4 flex items-center gap-2 rounded-xl bg-[#FEF2F2] px-3 py-2 text-xs font-semibold text-[#DC2626]">
          <Icon icon="lucide:alert-circle" className="h-4 w-4 shrink-0" />
          {submitError}
        </p>
      )}

      {batchProgress === null && hasStagedFiles && (
        <div className="mt-6 grid grid-cols-2 gap-3">
          <Button
            variant="outline"
            onClick={clearFiles}
            className="!h-11 !rounded-xl !border-2 !border-[#7B6D9B] !bg-white !text-sm !font-extrabold !text-[#0A2947] hover:!border-[#7B6D9B] hover:!bg-[#F7F7F7] hover:!text-[#0A2947]"
          >
            Cancel
          </Button>
          <Button
            onClick={handleUpload}
            disabled={hasOversizedStaged}
            className="!h-11 !rounded-xl !bg-[#F17893] !text-sm !font-extrabold !text-white hover:!bg-[#E85D7C] disabled:!cursor-not-allowed disabled:!opacity-50"
          >
            Upload
          </Button>
        </div>
      )}
    </AppModal>
  );
}
